import type { Session } from '@supabase/supabase-js';

import type { Profile, TenantId } from '@/types';

/** Claims Instoque adds to the Supabase access token. */
export interface InstoqueJwtClaims {
  readonly tenant_id?: unknown;
  readonly profile?: unknown;
  readonly app_metadata?: unknown;
  readonly role?: unknown;
  readonly sub?: unknown;
  readonly exp?: unknown;
}

/**
 * The profiles the access-token hook can emit. Anything outside this list is treated as no
 * profile at all — see `parseProfile`.
 *
 * This list has to track `Profile` in `apps/api/app/modules/identity/profiles.py`. When
 * `platform_admin` was added there it was not added here, and because an unknown profile
 * parses to `null` and every consumer reads `null` as unauthorised, the API granted that
 * profile everything while the application refused it the login. Failing closed is right;
 * the list going stale is what was wrong.
 */
export const PROFILES = [
  'operator',
  'buyer',
  'commercial_backoffice',
  'procurement',
  'controllership',
  'platform_admin',
] as const;

function decodeBase64Url(segment: string): string | null {
  const padded = segment
    .replace(/-/gu, '+')
    .replace(/_/gu, '/')
    .padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=');

  try {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Reads the payload of a JWT without verifying it. Verification is the API's job — the SPA only
 * needs the claims to render, and a forged token buys nothing because every request is
 * re-validated server-side.
 */
export function decodeJwtClaims(accessToken: string): InstoqueJwtClaims | null {
  const segments = accessToken.split('.');
  const payload = segments[1];
  if (segments.length !== 3 || payload === undefined) {
    return null;
  }

  const json = decodeBase64Url(payload);
  if (json === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null ? (parsed as InstoqueJwtClaims) : null;
  } catch {
    return null;
  }
}

/**
 * Both claims are also accepted under `app_metadata`, which is where Supabase nests them when the
 * access-token hook copies user metadata instead of writing a top-level claim. `apps/api`'s
 * `core/security.py` reads the same two places, so client and server can never disagree about
 * which value is in force. `user_metadata` is deliberately *not* consulted: it is writable by the
 * user themselves.
 */
function readClaim(claims: InstoqueJwtClaims | null, name: 'tenant_id' | 'profile'): unknown {
  if (claims === null) {
    return undefined;
  }
  if (claims[name] !== undefined) {
    return claims[name];
  }

  const metadata = claims.app_metadata;
  return typeof metadata === 'object' && metadata !== null
    ? (metadata as Record<string, unknown>)[name]
    : undefined;
}

/**
 * The tenant is read from the `tenant_id` claim and from nowhere else — not from the URL, not
 * from local storage, not from a user-editable profile row. The claim is the only value the API
 * also sees and validates, so any other source would let the client and the server disagree
 * about which tenant a request belongs to (ADR-0010).
 */
export function readTenantId(session: Session | null): TenantId | null {
  if (session === null) {
    return null;
  }

  const tenantId = readClaim(decodeJwtClaims(session.access_token), 'tenant_id');

  return typeof tenantId === 'string' && tenantId.trim() !== '' ? tenantId : null;
}

/**
 * Narrows an arbitrary claim value to a known profile. An absent, malformed or unrecognised
 * value returns `null` — never a default. Defaulting to the least-privileged profile would still
 * be a decision the token did not make, and would silently grant a screen to a user whose claim
 * failed to be injected; the caller must treat `null` as "not authorised" instead.
 */
export function parseProfile(value: unknown): Profile | null {
  if (typeof value !== 'string') {
    return null;
  }

  const candidate = value.trim();
  return (PROFILES as readonly string[]).includes(candidate) ? (candidate as Profile) : null;
}

/** Same rule as `readTenantId`: the profile comes from the verified claim and from nowhere else. */
export function readProfile(session: Session | null): Profile | null {
  if (session === null) {
    return null;
  }

  return parseProfile(readClaim(decodeJwtClaims(session.access_token), 'profile'));
}
